import React, {useContext, useEffect, useState} from "react";
import styled from "styled-components";
import Select from "react-select";
import {Tab, TabList, TabPanel} from "react-tabs";
import Box from "@mui/material/Box";
import {useQuery, useQueryClient} from "react-query";
import {customTheme} from "../../../../../IntReports/LocationTab";
import {TabsStyled} from "../thirdStep/BluePrintCategoriesRow";
import apiService from "../../../../../../../api/apiService";
import {partnersList, ucAccounts, dealRegistrations, userDetails} from "../../../../../../../utils/const/query-key";
import TechnicalEngagementContext from "../../TechnicalEngagementContext";
import EnvironmentContext from "../../EnvironmentContext";


const Form = styled.form`
  width: 500px;

  .input_text_container {
    margin-bottom: 20px;
  }

  .input_customer_name {
    width: 100%;
    padding: 10px;
    border-radius: 4px;
    border: 1px solid var(--color-toggle-border);

    &:focus-visible {
      outline-color: #EC407A;
    }
  }
`;

export const Description = styled.div`
  font-size: 14px;
  color: var(--color-form-font-description);
  margin-bottom: 5px;
`;

const TabListContainer = styled(TabList)`
  display: flex;
  padding: 0px;
  justify-content: space-between;
`;

const PartnerTabStyle = styled(Tab)`
  flex: 1;
  padding: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  list-style: none;
  cursor: pointer;
  background: white;
  border: 1px solid #D9D9D9;
  color: grey;
`;

const PartnerTabPanelContent = styled.div`
  padding-top: 12px;
`;

const StepContainer = styled.div`
  display: flex;
`;

const Button = styled.button`
  width: 75px;
  height: 36px;
  background: var(--color-workspace-bg-page-area);
  border: 1px solid var(--color-createenv-form-btn-border);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;

  &:hover {
    background: var(--color-table-even);
    color: var(--color-brand-pink-primary);
  }

  &.next {
    background: var(--unnamed-color-ec407a);
    background: var(--color-brand-pink-primary);
    color: var(--color-workspace-bg-page-area);
    border-radius: 2px;
    margin-right: 17px;

    ${props => props.disabled ? 'pointer-events: none;' : ''}
    ${props => props.disabled ? 'background: var(--unnamed-color-ec407a) ;' : ''}
    ${props => props.disabled ? 'background: #f1d8df;' : ''}
    &:hover {
      background: var(--color-createenv-stepper-next-btn-bg-hover);
    }
  }
`;

const PartnerCustomerDemoForm = ({
    onSubmit,
    getRegionsData,
    regionSelected,
    handleRegionSelected,
    activeStep,
    handleStepBack,
}) => {
    const {technicalEngagement, setTechnicalEngagement} = useContext(TechnicalEngagementContext);
    const {environment, setEnvironment} = useContext(EnvironmentContext);

    // Derive initial tab states from persisted context so state survives step navigation
    const hasAccount = Boolean(technicalEngagement.accountName);
    const [partnerValue, setPartnerValue] = useState(null);
    const [customerTypeTabIndex, setCustomerTypeTabIndex] = useState(!hasAccount && environment.customer_company_name ? 1 : 0);
    const [ucAccountValue, setUcAccountValue] = useState(null);
    // Restore linkDemoTabIndex: if an opportunity is saved it means Deal Registration (0) was used;
    // if only a salesforceAccountID is present (no opportunity) then UC Account (1) was used.
    const [linkDemoTabIndex, setLinkDemoTabIndex] = useState(
        (environment.salesforceAccountID && !environment.salesforceOpportunityID) ? 1 : 0
    );
    const [dealRegistrationValue, setDealRegistrationValue] = useState(null);
    const [newCustomerName, setNewCustomerName] = useState(!hasAccount ? (environment.customer_company_name ?? "") : "");
    const queryClient = useQueryClient();
    const userData = queryClient.getQueryData(userDetails);

    const {data: partnersData, isLoading: isLoadingPartners} = useQuery(
        partnersList,
        () => apiService.getPartners()
    );

    const {data: ucAccountsData, isLoading: isLoadingAccounts} = useQuery(
        ucAccounts,
        () => apiService.getUCAccounts()
    );

    const {data: dealRegistrationsData = [], isLoading: isLoadingDealRegistrations, isFetching: isFetchingDealRegistrations, isSuccess: isDealRegSuccess} = useQuery(
        [dealRegistrations, partnerValue?.value, ucAccountValue?.value],
        () => apiService.getDealRegistrationsByPartnerAndAccount(partnerValue.value, ucAccountValue.value),
        {
            enabled: Boolean(partnerValue?.value && ucAccountValue?.value),
            select: (data) => Array.isArray(data) ? data : [],
            retry: false,
            onError: (err) => console.error('Failed to load deal registrations:', err),
        }
    );

    useEffect(() => {
        if (userData?.userEmail) {
            setTechnicalEngagement(prev => ({...prev, partnerEmail: userData.userEmail}));
        }
    }, [userData?.userEmail, setTechnicalEngagement]);

    // Restore partner dropdown selection after remount
    useEffect(() => {
        if (partnersData && technicalEngagement.partner && !partnerValue) {
            const found = partnersData.find(p => String(p.id) === String(technicalEngagement.partner));
            if (found) setPartnerValue({label: `${found.name} (${found.id})`, value: String(found.id), name: found.name});
        }
    }, [partnersData, technicalEngagement.partner, partnerValue]);

    // Restore UC account dropdown selection after remount.
    // Falls back to technicalEngagement.accountName when salesforceAccountID was cleared
    // on submit (DR path) but the user navigated back from step 3.
    useEffect(() => {
        const accountId = environment.salesforceAccountID || technicalEngagement.accountName;
        if (ucAccountsData && accountId && !ucAccountValue) {
            const found = ucAccountsData.find(a => String(a.id) === String(accountId));
            if (found) setUcAccountValue({label: `${found.accountName} (${found.id})`, value: String(found.id), name: found.accountName});
        }
    }, [ucAccountsData, environment.salesforceAccountID, technicalEngagement.accountName, ucAccountValue]);

    // Restore deal registration dropdown selection after remount
    useEffect(() => {
        if (dealRegistrationsData && technicalEngagement.opportunity && !dealRegistrationValue) {
            const found = dealRegistrationsData.find(d => String(d.oppSFId) === String(technicalEngagement.opportunity));
            if (found) setDealRegistrationValue({label: `${found.resellerCompanyName} (${found.id})`, value: String(found.id), oppSFId: String(found.oppSFId)});
        }
    }, [dealRegistrationsData, technicalEngagement.opportunity, dealRegistrationValue]);



    const partnerOptions = [...new Map(partnersData?.map(p => [p.id, p]) ?? []).values()]
        .map(p => ({label: `${p.name} (${p.id})`, value: String(p.id), name: p.name}));

    const ucAccountOptions = [...new Map(ucAccountsData?.map(a => [a.id, a]) ?? []).values()]
        .map(a => ({label: `${a.accountName} (${a.id})`, value: String(a.id), name: a.accountName}));

    const dealRegistrationOptions = [...new Map(dealRegistrationsData?.map(d => [d.id, d]) ?? []).values()]
        .map(d => ({label: `${d.resellerCompanyName} (${d.id})`, value: String(d.id), oppSFId: String(d.oppSFId)}));


    /**
     * Resets account-related fields in both contexts.
     * Used when the partner or UC account selection is cleared.
     */
    const resetAccountContext = () => {
        setTechnicalEngagement(prev => ({...prev, accountName: "", opportunity: ""}));
        setEnvironment(prev => ({...prev, customer_company_name: "", salesforceAccountID: ""}));
    };


    const handlePartnerChange = (val) => {
        setPartnerValue(val);
        setUcAccountValue(null);
        setDealRegistrationValue(null);
        resetAccountContext();
        setTechnicalEngagement(prev => ({...prev, partner: val ? val.value : ""}));
    };

    /**
     * UC Account selected:
     *  • Sets technicalEngagement.accountName  → triggers TE creation in BluePrintCategoriesRow
     *  • Clears any prior DR / opportunity selection
     *  • Syncs customer_company_name + salesforceAccountID in EnvironmentContext
     */
    const handleUcAccountChange = (val) => {
        setUcAccountValue(val);
        setDealRegistrationValue(null);
        if (val) {
            setTechnicalEngagement(prev => ({...prev, accountName: val.value, opportunity: ""}));
            setEnvironment(prev => ({...prev, customer_company_name: val.name, salesforceAccountID: val.value}));
        } else {
            resetAccountContext();
        }
    };

    /**
     * "Link Demo to" tab changed:
     *  • UC Account (1) → link TE to account only; clear opportunity
     */
    const handleLinkDemoTabChange = (index) => {
        setLinkDemoTabIndex(index);
        if (index === 1 && ucAccountValue) {
            setDealRegistrationValue(null);
            setTechnicalEngagement(prev => ({...prev, opportunity: ""}));
            setEnvironment(prev => ({...prev, salesforceOpportunityID: ""}));
        }
    };

    const handleDealRegistrationChange = (val) => {
        setDealRegistrationValue(val);
        const oppId = val ? (val.oppSFId ?? "") : "";
        // Keep accountName so the UC account dropdown can be restored on back-navigation
        setTechnicalEngagement(prev => ({...prev, opportunity: oppId}));
        setEnvironment(prev => ({...prev, salesforceOpportunityID: oppId, salesforceAccountID: ""}));
    };

    /**
     * Customer Type tab changed:
     *  • New Customer (1) → no SF account, so clear TE + env account data (no TE creation)
     *  • Existing Customer (0) → restore context from current ucAccountValue if still selected
     */
    const handleCustomerTypeTabChange = (index) => {
        setCustomerTypeTabIndex(index);
        if (index === 1) {
            setTechnicalEngagement(prev => ({...prev, accountName: "", opportunity: ""}));
            setEnvironment(prev => ({...prev, salesforceOpportunityID: "", customer_company_name:"", salesforceAccountID: ""}));
        } else {
            if (ucAccountValue) {
                setTechnicalEngagement(prev => ({
                    ...prev,
                    accountName: ucAccountValue.value,
                    opportunity: linkDemoTabIndex === 1 && dealRegistrationValue
                        ? (dealRegistrationValue.oppSFId ?? "")
                        : "",
                }));
                setEnvironment(prev => ({
                    ...prev,
                    customer_company_name: ucAccountValue.name,
                    salesforceAccountID: ucAccountValue.value,
                    opportunity: linkDemoTabIndex === 1 && dealRegistrationValue
                        ? (dealRegistrationValue.oppSFId ?? "")
                        : "",
                }));
            }
        }
    };


    /**
     * Determines whether the "Next" button should be disabled.
     * All of the following must be satisfied to enable the button:
     *  - A partner must be selected.
     *  - A cloud region must be selected.
     *  - Existing Customer (tab 0): a UC Account must be selected.
     *  - Existing Customer + Deal Registration (tab 0 + link tab 0): a Deal Registration must be selected.
     *  - New Customer (tab 1): a customer name must be entered.
     */
    const isNextDisabled =
        partnerValue === null ||
        regionSelected === "" ||
        (customerTypeTabIndex === 0 && ucAccountValue === null) ||
        (customerTypeTabIndex === 0 && linkDemoTabIndex === 0 && dealRegistrationValue === null) ||
        (customerTypeTabIndex === 1 && newCustomerName === "");

    return (
        <Form onSubmit={onSubmit} id={"second_step_form_partner"}>
            <div className={"input_text_container"}>
                <Description>
                    Partner <span style={{color: "red"}}>*</span>
                </Description>
                <Select
                    placeholder={<div>Select Partner</div>}
                    theme={customTheme}
                    options={partnerOptions}
                    value={partnerValue}
                    onChange={handlePartnerChange}
                    isLoading={isLoadingPartners}
                    id={"partner_dropdown"}
                />
            </div>
            <div className={"input_text_container"}>
                <Description>
                    Customer Type<span style={{color: "red"}}>*</span>
                </Description>
                <TabsStyled
                    selectedTabClassName={"selected"}
                    onSelect={handleCustomerTypeTabChange}
                    selectedIndex={customerTypeTabIndex}
                >
                    <TabListContainer>
                        <PartnerTabStyle className={"existing-customer-tab"}>Existing Customer</PartnerTabStyle>
                        <PartnerTabStyle className={"new-customer-tab"}>New Customer</PartnerTabStyle>
                    </TabListContainer>
                    <TabPanel>
                        <PartnerTabPanelContent>
                            <Description>
                                UC Account <span style={{color: "red"}}>*</span>
                            </Description>
                            <Select
                                placeholder={<div>Select</div>}
                                theme={customTheme}
                                options={ucAccountOptions}
                                value={ucAccountValue}
                                onChange={handleUcAccountChange}
                                isLoading={isLoadingAccounts}
                                id={"uc_account_dropdown"}
                            />
                            <Description style={{marginTop: "16px"}}>
                                Link Demo to<span style={{color: "red"}}>*</span>
                            </Description>
                            <TabsStyled
                                selectedTabClassName={"selected"}
                                onSelect={handleLinkDemoTabChange}
                                selectedIndex={linkDemoTabIndex}
                            >
                                <TabListContainer>
                                    <PartnerTabStyle className={"uc-account-tab"}>Deal Registration</PartnerTabStyle>
                                    <PartnerTabStyle className={"deal-registration-tab"}>UC Account</PartnerTabStyle>
                                </TabListContainer>
                                <TabPanel>
                                    <PartnerTabPanelContent>
                                        <Select
                                            placeholder={<div>Select Deal Registration</div>}
                                            theme={customTheme}
                                            options={dealRegistrationOptions}
                                            value={dealRegistrationValue}
                                            onChange={handleDealRegistrationChange}
                                            isLoading={isLoadingDealRegistrations}
                                            isDisabled={!partnerValue || !ucAccountValue}
                                            id={"deal_registration_dropdown"}
                                        />
                                        {isDealRegSuccess && !isFetchingDealRegistrations && dealRegistrationOptions.length === 0 &&
                                            <div style={{fontSize: '14px', color: 'red', marginTop: '6px'}}>
                                                No deal registrations found. Try linking via UC Account tab.
                                            </div>
                                        }
                                    </PartnerTabPanelContent>
                                </TabPanel>
                                <TabPanel>
                                    <div style={{marginBottom: "16px"}}></div>
                                </TabPanel>
                            </TabsStyled>
                        </PartnerTabPanelContent>
                    </TabPanel>
                    <TabPanel>
                        <PartnerTabPanelContent>
                            <Description>
                                Customer Name <span style={{color: "red"}}>*</span>
                            </Description>
                            <input
                                className={"input_customer_name"}
                                type={"text"}
                                maxLength={"35"}
                                placeholder={"Type customer name"}
                                value={newCustomerName}
                                onChange={(e) => {
                                    const name = e.target.value;
                                    setNewCustomerName(name);
                                    // Keep EnvironmentContext in sync so SecondStep's onSubmit picks it up
                                    setEnvironment(prev => ({...prev, customer_company_name: name}));
                                }}
                            />
                        </PartnerTabPanelContent>
                    </TabPanel>
                </TabsStyled>
            </div>
            <div className={"input_text_container"}>
                <Description>
                    Cloud Region <span style={{color: "red"}}>*</span>
                </Description>
                <Select
                    placeholder={<div>Select Region</div>}
                    theme={customTheme}
                    options={getRegionsData?.map(region => ({label: region.name, value: region.id}))}
                    onChange={handleRegionSelected}
                    value={regionSelected}
                    id={"region_dropdown"}
                />
            </div>
            <Box sx={{mb: 2}}>
                <StepContainer>
                    <Button
                        className={'next'}
                        sx={{mt: 1, mr: 1}}
                        disabled={isNextDisabled}
                        type={"submit"}
                    >
                        Next
                    </Button>
                    <Button
                        className={"back"}
                        onClick={handleStepBack}
                        sx={{mt: 1, mr: 1}}
                        type="button"
                    >
                        {activeStep === 0 ? 'Cancel' : 'Back'}
                    </Button>
                </StepContainer>
            </Box>
        </Form>
    );
};

export default PartnerCustomerDemoForm;
